import { create } from 'zustand';

export type NaphthaleneStage =
  | 'setup'
  | 'naphthalene-added'
  | 'thermometer-inserted'
  | 'water-poured'
  | 'tube-immersed'
  | 'heating'
  | 'melting'
  | 'transfer-ready'
  | 'transferring'
  | 'cooling'
  | 'freezing'
  | 'completed';

export type CameraView = 'default' | 'top' | 'side' | 'closeup' | 'workbench';

export type RecordedDataPoint = {
  time: number;
  temp: number;
  state: 'Solid' | 'Solid+Liquid' | 'Liquid';
};

interface NaphthaleneState {
  labStage: NaphthaleneStage;

  experimentTime: number;
  currentTemperature: number;
  waterBathTemp: number;
  meltFraction: number;

  isHeating: boolean;
  heatIntensity: number;
  isStirring: boolean;

  isAddingNaphthalene: boolean;
  addNaphthaleneProgress: number;
  isInsertingThermometer: boolean;
  thermometerProgress: number;
  isPouringWater: boolean;
  pourProgress: number;
  isImmersingTube: boolean;
  immersionProgress: number;
  isTransferring: boolean;
  transferProgress: number;

  recordedData: RecordedDataPoint[];
  lastRecordedTime: number;

  simulationSpeed: number;
  cameraView: CameraView;
  cameraVersion: number;
  isAutoDemoRunning: boolean;
  showTutorial: boolean;

  addNaphthaleneToTube: () => void;
  insertThermometer: () => void;
  pourWater: () => void;
  immerseTube: () => void;
  toggleHeating: () => void;
  setHeatIntensity: (i: number) => void;
  setStirring: (active: boolean) => void;
  recordReading: () => void;
  transferToFlask: () => void;

  setSimulationSpeed: (s: number) => void;
  cycleSimulationSpeed: () => void;
  setCameraView: (v: CameraView) => void;
  setAutoDemoRunning: (running: boolean) => void;
  setShowTutorial: (show: boolean) => void;

  tick: (delta: number) => void;
  reset: () => void;
}

const MELTING_POINT = 80.2;
const AMBIENT_TEMP = 25.0;
const SPEED_CYCLE = [1, 2, 5, 10];

const ADD_NAPH_DURATION = 2.0;
const INSERT_THERM_DURATION = 1.5;
const POUR_WATER_DURATION = 2.4;
const IMMERSE_DURATION = 1.6;
const TRANSFER_DURATION = 2.6;
const TRANSFER_TEMP_THRESHOLD = 95;

export const useNaphthaleneStore = create<NaphthaleneState>((set, get) => ({
  labStage: 'setup',
  experimentTime: 0,
  currentTemperature: AMBIENT_TEMP,
  waterBathTemp: AMBIENT_TEMP,
  meltFraction: 0,

  isHeating: false,
  heatIntensity: 1.0,
  isStirring: false,

  isAddingNaphthalene: false,
  addNaphthaleneProgress: 0,
  isInsertingThermometer: false,
  thermometerProgress: 0,
  isPouringWater: false,
  pourProgress: 0,
  isImmersingTube: false,
  immersionProgress: 0,
  isTransferring: false,
  transferProgress: 0,

  recordedData: [],
  lastRecordedTime: 0,

  simulationSpeed: 1,
  cameraView: 'default',
  cameraVersion: 0,
  isAutoDemoRunning: false,
  showTutorial: false,

  addNaphthaleneToTube: () => set((s) => {
    if (s.labStage !== 'setup' || s.isAddingNaphthalene) return {};
    return { isAddingNaphthalene: true, addNaphthaleneProgress: 0 };
  }),

  insertThermometer: () => set((s) => {
    if (s.labStage !== 'naphthalene-added' || s.isInsertingThermometer) return {};
    return { isInsertingThermometer: true, thermometerProgress: 0 };
  }),

  pourWater: () => set((s) => {
    if (s.labStage !== 'thermometer-inserted' || s.isPouringWater) return {};
    return { isPouringWater: true, pourProgress: 0 };
  }),

  immerseTube: () => set((s) => {
    if (s.labStage !== 'water-poured' || s.isImmersingTube) return {};
    return { isImmersingTube: true, immersionProgress: 0 };
  }),

  transferToFlask: () => set((s) => {
    if (s.labStage !== 'transfer-ready' || s.isTransferring) return {};
    return { isTransferring: true, transferProgress: 0 };
  }),

  toggleHeating: () => set((s) => {
    const allowed = s.labStage === 'tube-immersed' || s.labStage === 'heating' || s.labStage === 'melting';
    if (!allowed) return {};
    const nextHeating = !s.isHeating;
    return {
      isHeating: nextHeating,
      labStage: s.labStage === 'tube-immersed' && nextHeating ? 'heating' : s.labStage,
    };
  }),

  setHeatIntensity: (intensity) => set({ heatIntensity: Math.max(0, Math.min(1, intensity)) }),

  setStirring: (active) => set({ isStirring: active }),

  recordReading: () => set((s) => {
    const allowed = s.labStage === 'heating' || s.labStage === 'melting'
      || s.labStage === 'cooling' || s.labStage === 'freezing'
      || s.labStage === 'transfer-ready';
    if (!allowed) return {};

    const lastRec = s.recordedData.length > 0 ? s.recordedData[s.recordedData.length - 1].time : -1;
    const roundedTime = Math.floor(s.experimentTime);
    if (lastRec === roundedTime) return {};

    let stateOfMatter: RecordedDataPoint['state'] = 'Solid';
    if (s.meltFraction > 0 && s.meltFraction < 1) stateOfMatter = 'Solid+Liquid';
    else if (s.meltFraction >= 1) stateOfMatter = 'Liquid';

    const newRecord: RecordedDataPoint = {
      time: roundedTime,
      temp: parseFloat(s.currentTemperature.toFixed(1)),
      state: stateOfMatter,
    };

    return {
      recordedData: [...s.recordedData, newRecord],
      lastRecordedTime: s.experimentTime,
    };
  }),

  setSimulationSpeed: (s) => set({ simulationSpeed: Math.max(1, Math.min(10, s)) }),

  cycleSimulationSpeed: () => set((state) => {
    const idx = SPEED_CYCLE.indexOf(state.simulationSpeed);
    const next = SPEED_CYCLE[(idx + 1) % SPEED_CYCLE.length];
    return { simulationSpeed: next };
  }),

  setCameraView: (v) => set((s) => ({ cameraView: v, cameraVersion: s.cameraVersion + 1 })),
  setAutoDemoRunning: (running) => set({ isAutoDemoRunning: running }),
  setShowTutorial: (show) => set({ showTutorial: show }),

  tick: (delta) => set((s) => {
    const realDelta = delta / Math.max(0.0001, s.simulationSpeed);

    if (s.isAddingNaphthalene) {
      const p = Math.min(1, s.addNaphthaleneProgress + realDelta / ADD_NAPH_DURATION);
      if (p >= 1) return { isAddingNaphthalene: false, addNaphthaleneProgress: 1, labStage: 'naphthalene-added' };
      return { addNaphthaleneProgress: p };
    }
    if (s.isInsertingThermometer) {
      const p = Math.min(1, s.thermometerProgress + realDelta / INSERT_THERM_DURATION);
      if (p >= 1) return { isInsertingThermometer: false, thermometerProgress: 1, labStage: 'thermometer-inserted' };
      return { thermometerProgress: p };
    }
    if (s.isPouringWater) {
      const p = Math.min(1, s.pourProgress + realDelta / POUR_WATER_DURATION);
      if (p >= 1) return { isPouringWater: false, pourProgress: 1, labStage: 'water-poured' };
      return { pourProgress: p };
    }
    if (s.isImmersingTube) {
      const p = Math.min(1, s.immersionProgress + realDelta / IMMERSE_DURATION);
      if (p >= 1) return { isImmersingTube: false, immersionProgress: 1, labStage: 'tube-immersed' };
      return { immersionProgress: p };
    }
    if (s.isTransferring) {
      const p = Math.min(1, s.transferProgress + realDelta / TRANSFER_DURATION);
      if (p >= 1) {
        return {
          isTransferring: false,
          transferProgress: 1,
          labStage: 'cooling',
          isHeating: false,
          experimentTime: 0,
        };
      }
      return { transferProgress: p };
    }

    const physicsActive =
      s.labStage === 'heating' || s.labStage === 'melting' ||
      s.labStage === 'transfer-ready' || s.labStage === 'cooling' ||
      s.labStage === 'freezing' || s.isHeating;
    if (!physicsActive) return {};

    const newTime = s.experimentTime + delta;

    const maxWaterTemp = AMBIENT_TEMP + s.heatIntensity * 120;
    let newWaterTemp = s.waterBathTemp;
    if (s.isHeating) {
      newWaterTemp += (maxWaterTemp - s.waterBathTemp) * 0.05 * delta;
    } else {
      newWaterTemp -= (s.waterBathTemp - AMBIENT_TEMP) * 0.02 * delta;
    }
    if (newWaterTemp >= 100) newWaterTemp = 100;
    if (newWaterTemp < AMBIENT_TEMP) newWaterTemp = AMBIENT_TEMP;

    let nextNaphTemp = s.currentTemperature;
    let nextMeltFraction = s.meltFraction;

    const isImmersedInBath =
      s.labStage === 'heating' || s.labStage === 'melting' || s.labStage === 'transfer-ready';
    const sourceTemp = isImmersedInBath ? newWaterTemp : AMBIENT_TEMP;

    const baseTransferRate = isImmersedInBath ? 0.04 : 0.015;
    const transferRate = s.isStirring ? baseTransferRate * 1.5 : baseTransferRate;

    const heatFlow = (sourceTemp - nextNaphTemp) * transferRate * delta;

    if (heatFlow > 0) {
      if (nextNaphTemp < MELTING_POINT) {
        nextNaphTemp += heatFlow;
        if (nextNaphTemp > MELTING_POINT) {
          const excessHeat = nextNaphTemp - MELTING_POINT;
          nextNaphTemp = MELTING_POINT;
          nextMeltFraction += excessHeat * 0.3;
        }
      } else if (nextNaphTemp === MELTING_POINT && nextMeltFraction < 1.0) {
        nextMeltFraction += heatFlow * 0.3;
        if (nextMeltFraction >= 1.0) nextMeltFraction = 1.0;
      } else {
        nextNaphTemp += heatFlow;
      }
    } else if (heatFlow < 0) {
      if (nextNaphTemp > MELTING_POINT) {
        nextNaphTemp += heatFlow;
        const superCoolLimit = s.isStirring ? MELTING_POINT : MELTING_POINT - 1.2;
        if (nextNaphTemp < superCoolLimit) nextNaphTemp = superCoolLimit;
        if (nextNaphTemp <= MELTING_POINT && s.meltFraction > 0 && s.isStirring) {
          nextNaphTemp = MELTING_POINT;
        }
      } else if (nextNaphTemp <= MELTING_POINT && nextMeltFraction > 0.0) {
        if (s.isStirring) nextNaphTemp = MELTING_POINT;
        nextMeltFraction += heatFlow * 0.3;
        if (nextMeltFraction <= 0.0) {
          nextMeltFraction = 0.0;
          nextNaphTemp = MELTING_POINT - 0.1;
        }
      } else {
        nextNaphTemp += heatFlow;
      }
    }

    let newStage: NaphthaleneStage = s.labStage;
    if (newStage === 'heating' && nextMeltFraction >= 1.0) {
      newStage = 'melting';
    }
    if (newStage === 'melting' && nextNaphTemp >= TRANSFER_TEMP_THRESHOLD) {
      newStage = 'transfer-ready';
    }
    if (newStage === 'cooling' && nextNaphTemp <= MELTING_POINT && nextMeltFraction < 1.0 && nextMeltFraction > 0) {
      newStage = 'freezing';
    }
    let nextIsHeating = s.isHeating;
    if (newStage === 'freezing' && nextMeltFraction <= 0.0) {
      newStage = 'completed';
      nextIsHeating = false;
    }

    return {
      experimentTime: newTime,
      currentTemperature: nextNaphTemp,
      waterBathTemp: newWaterTemp,
      meltFraction: nextMeltFraction,
      labStage: newStage,
      isHeating: nextIsHeating,
    };
  }),

  reset: () => set({
    labStage: 'setup',
    experimentTime: 0,
    currentTemperature: AMBIENT_TEMP,
    waterBathTemp: AMBIENT_TEMP,
    meltFraction: 0,
    isHeating: false,
    heatIntensity: 1.0,
    isStirring: false,
    isAddingNaphthalene: false,
    addNaphthaleneProgress: 0,
    isInsertingThermometer: false,
    thermometerProgress: 0,
    isPouringWater: false,
    pourProgress: 0,
    isImmersingTube: false,
    immersionProgress: 0,
    isTransferring: false,
    transferProgress: 0,
    recordedData: [],
    lastRecordedTime: 0,
    simulationSpeed: 1,
    cameraView: 'default',
    isAutoDemoRunning: false,
    showTutorial: false,
  }),
}));
